import React from "react";
import {
  TableCompData,
  CardComp,
  ButtonComp,
  MoneyInputComp,
  SelectComp2,
  FormikDatePicker,
} from "../../../components";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import EmptyTable from "../../../components/Common/EmptyTable";
import { formatMoney } from "../../../utils";
import moment from "moment";
import { BsArrowLeft } from "react-icons/bs";
import { useNavigate, useParams } from "react-router-dom";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import {
  useGetLoanBreakdownByLoanRequestIdQuery,
  useRecordInformalLoanPaymentMutation,
} from "../../../store/admin/loanRepayment";
import { usePaymentMethodsQuery } from "../../../store/generalSelections/generalSelection";
import { useGetAllBankInfoQuery } from "../../../store/admin/settings/societyBankSlice";
import toast from "react-hot-toast";

/**
 * Informal (flexible) admin loan repayment breakdown.
 * Shows principal, rate, next interest application date; current principal, accrued interest, total balance;
 * payment history and record-payment form. No penalty or monthly target.
 */
const InformalSingleLoanRepaymentBreakdownByMember = ({
  data,
  isLoading,
}) => {
  const navigate = useNavigate();
  const { loanRequestId } = useParams();
  const [isSubmittingPayment, setIsSubmittingPayment] = React.useState(false);
  const [recordInformalPayment, { isLoading: isRecordingPayment }] = useRecordInformalLoanPaymentMutation();
  const { refetch: refetchBreakdown } = useGetLoanBreakdownByLoanRequestIdQuery(loanRequestId);
  const { data: paymentMethodsData } = usePaymentMethodsQuery();
  const { data: bankData, isLoading: isLoadingBank } = useGetAllBankInfoQuery({ pageNumber: 1 });

  const requiresBank = (paymentMethod) => {
    if (!paymentMethod) return false;
    const m = String(paymentMethod).toUpperCase();
    return m.includes("BANK") || m.includes("TRANSFER") || m === "CHEQUE";
  };

  const staffPaymentMethodOptions = React.useMemo(() => {
    const list = paymentMethodsData?.data ?? paymentMethodsData ?? [];
    const arr = Array.isArray(list) ? list : [];
    return arr
      .filter((item) => {
        const name = (item?.name ?? item?.label ?? "").toUpperCase();
        return !name.includes("PAYSTACK") && !name.includes("CARD");
      })
      .map((item) => ({
        label: item.name ?? item.label ?? item.value,
        value: item.name ?? item.value ?? item.id,
      }));
  }, [paymentMethodsData]);

  const bankOptions = React.useMemo(() => {
    const list = bankData?.company_banks ?? [];
    return (Array.isArray(list) ? list : []).map((item) => ({
      label: item.bank_name,
      value: item.bank_name,
    }));
  }, [bankData]);

  const currentBalance = Number(
    data?.total_balance ?? data?.outstanding ?? data?.remaining_balance ?? 0
  );

  const recordPaymentValidation = React.useMemo(
    () =>
      Yup.object({
        amount: Yup.string()
          .required("Required")
          .test(
            "maxBalance",
            `Amount cannot exceed current balance (${currentBalance > 0 ? formatMoney(currentBalance.toFixed(2)) : "—"})`,
            (value) => {
              if (currentBalance <= 0) return true;
              const num = Number(value?.replace?.(/,/g, "") ?? value);
              if (Number.isNaN(num) || num <= 0) return true;
              return num <= currentBalance;
            }
          ),
        payment_date: Yup.string().required("Required"),
        payment_method: Yup.string().required("Required"),
        bank: Yup.string().when("payment_method", {
          is: (v) => requiresBank(v),
          then: (s) => s.required("Bank is required for this payment method"),
          otherwise: (s) => s.notRequired(),
        }),
      }),
    [currentBalance]
  );

  const handleRecordPayment = async (values, { resetForm }) => {
    if (isSubmittingPayment || isRecordingPayment) return;
    setIsSubmittingPayment(true);
    const idempotencyKey = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const payload = {
      loan_request_id: Number(loanRequestId),
      amount: values.amount,
      payment_date: values.payment_date,
      payment_method: values.payment_method,
      idempotency_key: idempotencyKey,
    };
    if (requiresBank(values.payment_method) && values.bank) payload.bank = values.bank;
    try {
      const response = await recordInformalPayment(payload);
      const { data: resData, error } = response;
      if (error) {
        toast.error(error?.data?.message || "Failed to record payment");
        return;
      }
      if (resData?.status === false) {
        toast.error(resData?.message || "Failed to record payment");
        return;
      }
      toast.success(resData?.message || "Payment recorded");
      resetForm();
      refetchBreakdown();
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  const isLoanCompleted =
    data?.status === "PAID" || Number(data?.total_balance ?? data?.outstanding ?? data?.remaining_balance ?? 0) === 0;

  const paymentHistory = data?.payment_history ?? data?.payments ?? [];
  const columns = [
    {
      name: "S/N",
      selector: (_, index) => (
        <span className="text-muted">{index + 1}</span>
      ),
      width: "60px",
    },
    {
      name: "DATE",
      selector: (row) => (
        <span className="fw-2 text-black1">
          {row?.paid_at
            ? moment(row.paid_at).format("DD-MMM-YYYY")
            : row?.payment_date
            ? moment(row.payment_date).format("DD-MMM-YYYY")
            : "—"}
        </span>
      ),
      minWidth: "140px",
    },
    {
      name: "AMOUNT",
      selector: (row) => (
        <span className="fw-2 text-black1">
          {formatMoney(Number(row?.amount ?? 0).toFixed(2))}
        </span>
      ),
      minWidth: "160px",
    },
    {
      name: "BALANCE AFTER",
      selector: (row) => (
        <span className="text-muted">
          {row?.balance_after != null
            ? formatMoney(Number(row.balance_after).toFixed(2))
            : "—"}
        </span>
      ),
      minWidth: "140px",
    },
  ];

  return (
    <section className="px-lg-3 min-vh-100 mb-3">
      <div className="space-y-4">
        <CardComp cardClassName="border-0 shadow-sm mb-4">
          <div className="d-flex align-items-center gap-3">
            <BsArrowLeft
              size={24}
              onClick={() => navigate(-1)}
              className="cursor-pointer text-primary1"
            />
            <div className="flex-grow-1">
              <h4 className="fw-bold mb-1 text-primary1">
                {data?.loan_type?.name || "Loan"} (Flexible)
              </h4>
              {data?.member && (
                <p className="text-muted mb-0 small">
                  Member: {data.member.first_name} {data.member.last_name}
                  {data.member.member_no && ` (${data.member.member_no})`}
                </p>
              )}
            </div>
          </div>
        </CardComp>

        <CardComp cardClassName="border-0 shadow-sm mb-4">
          <div className="p-3">
            <p className="text-muted mb-2 small">
              Interest is applied monthly on the meeting date as{" "}
              <strong>
                {Number(data?.loan_type?.rate ?? data?.rate ?? 0).toFixed(1)}%
              </strong>{" "}
              of current balance.
            </p>
            <p className="text-muted mb-0 small">
              Next interest application date:{" "}
              <strong>
                {data?.next_interest_application_date
                  ? moment(data.next_interest_application_date).format("DD-MMM-YYYY")
                  : data?.next_evaluation_date
                  ? moment(data.next_evaluation_date).format("DD-MMM-YYYY")
                  : "—"}
              </strong>
            </p>
          </div>
          <div className="row g-4 p-3 pt-0">
            <div className="col-md-3 col-6">
              <div className="text-center">
                <p className="text-muted mb-2 small fw-semibold">Principal</p>
                <span className="text-3xl fw-bold text-dark">
                  {formatMoney(
                    Number(data?.principal ?? data?.loan_amount ?? data?.total_payable ?? 0).toFixed(2)
                  )}
                </span>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="text-center border-start border-end border-md-0 px-md-0 px-3">
                <p className="text-muted mb-2 small fw-semibold">Current principal</p>
                <span className="text-3xl fw-bold text-dark">
                  {formatMoney(
                    Number(data?.current_principal ?? data?.principal ?? data?.loan_amount ?? 0).toFixed(2)
                  )}
                </span>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="text-center border-start border-end border-md-0 px-md-0 px-3">
                <p className="text-muted mb-2 small fw-semibold">Accrued interest</p>
                <span className="text-3xl fw-bold text-secondary">
                  {formatMoney(Number(data?.accrued_interest ?? 0).toFixed(2))}
                </span>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="text-center border-start border-end border-md-0 px-md-0 px-3">
                <p className="text-muted mb-2 small fw-semibold">Total balance</p>
                <span className="text-3xl fw-bold text-danger">
                  {formatMoney(
                    Number(data?.total_balance ?? data?.outstanding ?? data?.remaining_balance ?? 0).toFixed(2)
                  )}
                </span>
              </div>
            </div>
          </div>
          <div className="row g-4 p-3 pt-0">
            <div className="col-md-4 col-6">
              <p className="text-muted mb-1 small fw-semibold">Total paid</p>
              <span className="fw-bold text-success">
                {formatMoney(Number(data?.total_paid ?? 0).toFixed(2))}
              </span>
            </div>
          </div>
        </CardComp>

        <CardComp cardClassName="border-0 shadow-sm mb-4">
          {isLoanCompleted ? (
            <div className="p-3">
              <h5 className="fw-bold mb-1 text-success">Completed</h5>
              <p className="text-muted mb-0 small">
                This loan has been fully repaid. No further payments are required.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-3 px-2 px-md-0">
                <h5 className="fw-bold mb-1 text-primary1">Record payment</h5>
                <p className="text-muted mb-0 small">
                  Enter amount and payment method (staff: CASH, BANK_TRANSFER, CHEQUE only).
                </p>
                {currentBalance > 0 && (
                  <p className="text-black1 mb-0 mt-1 small">
                    Current balance: <strong>{formatMoney(currentBalance.toFixed(2))}</strong>
                  </p>
                )}
              </div>
              <Formik
                initialValues={{
                  amount: "",
                  payment_date: moment().format("YYYY-MM-DD"),
                  payment_method: "",
                  bank: "",
                }}
                validationSchema={recordPaymentValidation}
                onSubmit={handleRecordPayment}
              >
                {({ values, errors, touched, setFieldValue }) => {
                  const showBankField = requiresBank(values.payment_method);
                  return (
                    <Form className="px-2 px-md-3 pb-3 pt-0">
                      <div className="row g-3">
                        <div className="col-12 col-md-6">
                          <MoneyInputComp
                            LabelText="Amount"
                            borderColor="#A1A0A0"
                            LabelClassName="fw-3"
                            placeholder="Enter amount"
                            name="amount"
                          />
                          {touched.amount && errors.amount && (
                            <small className="text-danger d-block mt-1">{errors.amount}</small>
                          )}
                        </div>
                        <div className="col-12 col-md-6">
                          <FormikDatePicker
                            fieldName="payment_date"
                            labelText="Payment date"
                            labelClassName="fw-3"
                          />
                          {touched.payment_date && errors.payment_date && (
                            <small className="text-danger d-block mt-1">{errors.payment_date}</small>
                          )}
                        </div>
                        <div className="col-12 col-md-6">
                          <SelectComp2
                            LabelClassName="fw-3"
                            placeholder="Select payment method"
                            labelText="Payment method"
                            arrayComp={staffPaymentMethodOptions}
                            selectedOption={values.payment_method}
                            setSelectedOption={(opt) => {
                              setFieldValue("payment_method", opt?.value ?? "");
                              if (!requiresBank(opt?.value)) setFieldValue("bank", "");
                            }}
                            errors={
                              touched.payment_method && errors.payment_method
                                ? [errors.payment_method]
                                : []
                            }
                          />
                        </div>
                        {showBankField && (
                          <div className="col-12 col-md-6">
                            <SelectComp2
                              LabelClassName="fw-3"
                              placeholder="Select bank"
                              labelText="Bank paid to"
                              arrayComp={bankOptions}
                              isLoading={isLoadingBank}
                              selectedOption={values.bank}
                              setSelectedOption={(opt) => setFieldValue("bank", opt?.value ?? "")}
                              errors={touched.bank && errors.bank ? [errors.bank] : []}
                            />
                          </div>
                        )}
                        <div className="col-12 mt-2">
                          <ButtonComp
                            type="submit"
                            loading={isRecordingPayment}
                            disabled={isSubmittingPayment}
                            btnText="Record payment"
                            btnClassName="bg-primary1 px-4 py-2 text-white rounded border-0 w-100"
                            style={{ minHeight: "44px" }}
                          />
                        </div>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </>
          )}
        </CardComp>

        <CardComp cardClassName="border-0 shadow-sm">
          <div className="mb-4">
            <h5 className="fw-bold mb-1 text-primary1">Payment History</h5>
            <p className="text-muted mb-0 small">
              All payments recorded for this loan
            </p>
          </div>
          <TableCompData
            marginBottom="1rem"
            columns={columns}
            data={paymentHistory}
            progressPending={isLoading}
            progressComponent={<TableSkeletonLoader rows={8} columns={4} />}
            message={
              !isLoading && (
                <EmptyTable Message="No payments recorded yet" />
              )
            }
          />
        </CardComp>
      </div>
    </section>
  );
};

export default InformalSingleLoanRepaymentBreakdownByMember;
